import { useEffect, useState } from "react";

interface AnimatedBackgroundProps {
  children: React.ReactNode;
  variant?: "hero" | "stats" | "minimal";
}

export function AnimatedBackground({ children, variant = "hero" }: AnimatedBackgroundProps) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const getBackgroundStyle = () => {
    const { x, y } = mousePosition;
    const xPercent = (x / window.innerWidth) * 100;
    const yPercent = (y / window.innerHeight) * 100;

    switch (variant) {
      case "hero":
        return {
          background: `
            radial-gradient(circle at ${xPercent}% ${yPercent}%, hsl(var(--primary) / 0.1) 0%, transparent 50%),
            radial-gradient(ellipse 800px 400px at 50% 120%, hsl(var(--primary-glow) / 0.05) 0%, transparent 50%),
            radial-gradient(ellipse 600px 300px at 0% 0%, hsl(var(--profit) / 0.03) 0%, transparent 50%),
            radial-gradient(ellipse 600px 300px at 100% 0%, hsl(var(--premium) / 0.03) 0%, transparent 50%),
            hsl(var(--background))
          `,
        };
      case "stats":
        return {
          background: `
            linear-gradient(45deg, transparent 30%, hsl(var(--muted) / 0.05) 50%, transparent 70%),
            radial-gradient(circle at ${xPercent}% ${yPercent}%, hsl(var(--primary) / 0.02) 0%, transparent 40%),
            hsl(var(--background))
          `,
        };
      default:
        return {
          background: `hsl(var(--background))`,
        };
    }
  };

  return (
    <div 
      className="relative transition-all duration-300 ease-out"
      style={getBackgroundStyle()}
    >
      {/* Animated floating elements */}
      {variant === "hero" && (
        <>
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary/20 rounded-full animate-float" 
               style={{ animationDelay: '0s' }} />
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-profit/30 rounded-full animate-float" 
               style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-premium/25 rounded-full animate-float" 
               style={{ animationDelay: '2s' }} />
          <div className="absolute top-2/3 right-1/4 w-1 h-1 bg-primary-glow/20 rounded-full animate-float" 
               style={{ animationDelay: '0.5s' }} />
        </>
      )}
      
      {children}
    </div>
  );
}